import React from 'react';

const HSEQPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-ocean-100">
      <div className="section">
        <h1 className="section-header">Health, Safety, Environmental & Quality</h1>
        
        <div className="maritime-card p-8 mb-16 text-center">
          <h2 className="text-2xl font-bold text-ocean-800 mb-4">Our Commitment to HSEQ Excellence</h2>
          <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
            At PetroMarine, we are committed to maintaining the highest standards of Health, Safety, 
            Environmental protection, and Quality in all our operations. Our comprehensive HSEQ 
            management system ensures zero harm to people and the environment while delivering 
            exceptional service quality.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div className="maritime-card p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd"/>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-ocean-800">Health & Safety</h2>
            </div>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Zero tolerance policy for unsafe practices and behaviors</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Comprehensive safety training programs for all personnel</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Regular safety audits and risk assessments</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Emergency response and crisis management procedures</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Occupational health monitoring and medical surveillance</span>
              </li>
            </ul>
          </div>

          <div className="maritime-card p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-ocean-800">Environmental Protection</h2>
            </div>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Marine pollution prevention and response programs</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Waste management and recycling initiatives</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Carbon footprint reduction strategies</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Biodiversity conservation efforts</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Environmental impact assessments</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="maritime-card p-8 mb-16">
          <h2 className="text-2xl font-bold text-ocean-800 mb-6 text-center">Quality Management System</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">Continuous Improvement</h3>
              <p className="text-gray-600 text-sm">Regular review and enhancement of all processes</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">Client Satisfaction</h3>
              <p className="text-gray-600 text-sm">Exceeding client expectations through quality service</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">Performance Monitoring</h3>
              <p className="text-gray-600 text-sm">Data-driven approach to quality assurance</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div className="maritime-card p-8">
            <h2 className="text-2xl font-bold text-ocean-800 mb-6">Safety Statistics</h2>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Lost Time Injury Frequency Rate</span>
                <span className="text-2xl font-bold text-green-600">0.00</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Total Recordable Injury Rate</span>
                <span className="text-2xl font-bold text-green-600">0.00</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Environmental Incidents</span>
                <span className="text-2xl font-bold text-green-600">0</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Client Satisfaction Rate</span>
                <span className="text-2xl font-bold text-ocean-600">98%</span>
              </div>
            </div>
          </div>

          <div className="maritime-card p-8">
            <h2 className="text-2xl font-bold text-ocean-800 mb-6">Certifications</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-ocean-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-ocean-600 mb-1">ISO</div>
                <div className="text-sm text-gray-600">9001:2015</div>
              </div>
              <div className="bg-ocean-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-ocean-600 mb-1">ISO</div>
                <div className="text-sm text-gray-600">14001:2015</div>
              </div>
              <div className="bg-ocean-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-ocean-600 mb-1">ISO</div>
                <div className="text-sm text-gray-600">45001:2018</div>
              </div>
              <div className="bg-ocean-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-ocean-600 mb-1">MLC</div>
                <div className="text-sm text-gray-600">2006</div>
              </div>
            </div>
          </div>
        </div>

        <div className="maritime-card p-8 text-center">
          <h2 className="text-2xl font-bold text-ocean-800 mb-4">HSEQ Policy Statement</h2>
          <p className="text-gray-700 max-w-4xl mx-auto leading-relaxed">
            "PetroMarine is committed to conducting all operations in a manner that protects the health 
            and safety of our employees, contractors, and the public, while safeguarding the environment 
            and delivering services of the highest quality. We strive for continuous improvement in our 
            HSEQ performance through employee engagement, stakeholder collaboration, and the implementation 
            of best practices."
          </p>
          <div className="mt-6">
            <p className="font-semibold text-ocean-800">Management Team</p>
            <p className="text-gray-600">PetroMarine Offshore Projects Support Co.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HSEQPage;